<script>
import { h } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

const OPERATOR_ICON_MAP = {
  equal_to: 'i-ph-equals-bold',
  not_equal_to: 'i-ph-not-equals-bold',
  is_present: 'i-ph-member-of-bold',
  is_not_present: 'i-ph-not-member-of-bold',
  contains: 'i-ph-superset-of-bold',
  does_not_contain: 'i-ph-not-superset-of-bold',
  is_greater_than: 'i-ph-greater-than-bold',
  is_less_than: 'i-ph-less-than-bold',
  days_before: 'i-ph-calendar-minus-bold',
  starts_with: 'i-ph-caret-line-right-bold',
  ends_with: 'i-ph-caret-line-left-bold',
};

export default {
  name: 'FilterInput',
  components: {
    NextButton,
    FilterSelect,
    SingleSelect,
    TagInput,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => {},
    },
    filterAttributes: {
      type: Array,
      default: () => [],
    },
    inputType: {
      type: String,
      default: 'plain_text',
    },
    operators: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    showQueryOperator: {
      type: Boolean,
      default: false,
    },
    showUserInput: {
      type: Boolean,
      default: true,
    },
    groupedFilters: {
      type: Boolean,
      default: false,
    },
    filterGroups: {
      type: Array,
      default: () => [],
    },
    customAttributeType: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'removeFilter', 'resetFilter'],
  data() {
    return {
      pendingTagInput: '',
    };
  },
  computed: {
    attributeKey: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.attribute_key;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, attribute_key: value });
      },
    },
    filterOperator: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.filter_operator;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, filter_operator: value });
      },
    },
    values: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.values;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, values: value });
      },
    },
    query_operator: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.query_operator;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, query_operator: value });
      },
    },
    custom_attribute_type: {
      get() {
        if (!this.customAttributeType) return '';
        return this.customAttributeType;
      },
      set() {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', {
          ...payload,
          custom_attribute_type: this.customAttributeType,
        });
      },
    },
    shouldShowUserInput() {
      const operatorsThatDontNeedInput = ['is_present', 'is_not_present'];
      return (
        this.showUserInput &&
        !operatorsThatDontNeedInput.includes(this.filterOperator)
      );
    },
    operatorOptions() {
      return (this.operators || []).map(op => ({
        value: op.value,
        label: this.$t(`FILTER.OPERATOR_LABELS.${op.value}`),
        icon: h('span', {
          class: `${OPERATOR_ICON_MAP[op.value] || 'i-ph-equals-bold'} !text-n-blue-11`,
        }),
      }));
    },
    attributeOptions() {
      if (this.groupedFilters) {
        return (this.filterGroups || []).flatMap(group =>
          (group.attributes || [])
            .filter(attribute => !attribute.disabled)
            .map(attribute => ({
              id: attribute.key,
              name: `${group.name} / ${attribute.name}`,
            }))
        );
      }

      return (this.filterAttributes || [])
        .filter(attribute => !attribute.disabled)
        .map(attribute => ({
          id: attribute.key,
          name: attribute.name,
        }));
    },
    attributeSelectModel: {
      get() {
        return (
          this.attributeOptions.find(option => option.id === this.attributeKey) ||
          null
        );
      },
      set(value) {
        this.attributeKey = value?.id || '';
      },
    },
    queryOperatorOptions() {
      return [
        {
          value: 'and',
          label: this.$t('FILTER.QUERY_DROPDOWN_LABELS.AND'),
        },
        {
          value: 'or',
          label: this.$t('FILTER.QUERY_DROPDOWN_LABELS.OR'),
        },
      ];
    },
    // Bridges the stored format (string or array) ↔ TagInput's Array<string>.
    // Handles legacy data where a single comma-separated string was stored (e.g. ["/a,/b,/c"]).
    valuesAsTagArray: {
      get() {
        const raw = this.values;
        if (!raw) return [];
        if (Array.isArray(raw)) {
          // Legacy: single element that is itself comma-separated
          if (
            raw.length === 1 &&
            typeof raw[0] === 'string' &&
            raw[0].includes(',')
          ) {
            return raw[0]
              .split(',')
              .map(v => v.trim())
              .filter(Boolean);
          }
          return raw.filter(Boolean);
        }
        if (typeof raw === 'string' && raw.includes(',')) {
          return raw
            .split(',')
            .map(v => v.trim())
            .filter(Boolean);
        }
        return raw ? [raw] : [];
      },
      set(tags) {
        this.pendingTagInput = '';
        this.values = tags;
      },
    },
    translatedErrorMessage() {
      if (!this.errorMessage) return '';

      const i18nKey = `AUTOMATION.ERRORS.${this.errorMessage}`;
      return this.$te(i18nKey) ? this.$t(i18nKey) : this.errorMessage;
    },
    shouldShowMultiValueHint() {
      const multiValueOps = [
        'equal_to',
        'not_equal_to',
        'contains',
        'does_not_contain',
      ];
      if (this.inputType === 'comma_separated_plain_text') {
        return (
          multiValueOps.includes(this.filterOperator) &&
          this.valuesAsTagArray.length > 1
        );
      }
      return (
        multiValueOps.includes(this.filterOperator) &&
        Array.isArray(this.values) &&
        this.values.length > 1
      );
    },
    multiValueLogicBadge() {
      return this.filterOperator === 'equal_to' ||
        this.filterOperator === 'contains'
        ? this.$t('FILTER.QUERY_DROPDOWN_LABELS.OR')
        : this.$t('FILTER.QUERY_DROPDOWN_LABELS.AND');
    },
  },
  watch: {
    customAttributeType: {
      handler(value) {
        if (
          value === 'conversation_attribute' ||
          value === 'contact_attribute'
        ) {
          // eslint-disable-next-line vue/no-mutating-props
          this.modelValue.custom_attribute_type = this.customAttributeType;
          // eslint-disable-next-line vue/no-mutating-props
        } else this.modelValue.custom_attribute_type = '';
      },
      immediate: true,
    },
  },
  methods: {
    handleAttributeChange(value) {
      this.attributeSelectModel = value;
      this.resetFilter();
    },
    onTagInput(value) {
      this.pendingTagInput = value || '';
    },
    commitPendingTagInput(rawValue = this.pendingTagInput) {
      if (this.inputType !== 'comma_separated_plain_text') return;

      const normalizedValues = `${rawValue || ''}`
        .split(',')
        .map(value => value.trim())
        .filter(Boolean);

      if (!normalizedValues.length) return;

      const currentValues = this.valuesAsTagArray;
      const nextValues = [...currentValues];

      normalizedValues.forEach(value => {
        if (!nextValues.includes(value)) {
          nextValues.push(value);
        }
      });

      this.valuesAsTagArray = nextValues;
    },
    removeFilter() {
      this.$emit('removeFilter');
    },
    resetFilter() {
      this.$emit('resetFilter');
    },
    getInputErrorClass(errorMessage) {
      return errorMessage
        ? 'bg-n-ruby-8/20 border-n-ruby-5 dark:border-n-ruby-5'
        : 'bg-n-background border-n-weak dark:border-n-weak';
    },
  },
};
</script>

<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div>
    <div
      class="p-2 border border-solid rounded-lg"
      :class="getInputErrorClass(errorMessage)"
    >
      <div class="flex gap-1">
        <SingleSelect
          v-model="attributeSelectModel"
          :options="attributeOptions"
          disable-deselect
          class="max-w-[30%] mr-1 automation-filter-input__select"
          @update:model-value="handleAttributeChange"
        />

        <FilterSelect
          v-if="operatorOptions.length > 0"
          v-model="filterOperator"
          variant="ghost"
          :options="operatorOptions"
        />

        <div
          v-if="shouldShowUserInput"
          class="flex-grow mr-1 filter__answer--wrap"
        >
          <div
            v-if="inputType === 'multi_select'"
            class="multiselect-wrap--small"
          >
            <multiselect
              v-model="values"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="dropdownValues"
              :allow-empty="false"
            >
              <template #noOptions>
                {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
              </template>
            </multiselect>
          </div>
          <div
            v-else-if="inputType === 'search_select'"
            class="multiselect-wrap--small"
          >
            <multiselect
              v-model="values"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="dropdownValues"
              :allow-empty="false"
              :option-height="104"
            >
              <template #noOptions>
                {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
              </template>
            </multiselect>
          </div>
          <div v-else-if="inputType === 'date'" class="multiselect-wrap--small">
            <input
              v-model="values"
              type="date"
              :editable="false"
              class="!mb-0 datepicker"
            />
          </div>
          <TagInput
            v-else-if="inputType === 'comma_separated_plain_text'"
            v-model="valuesAsTagArray"
            :placeholder="$t('FILTER.INPUT_PLACEHOLDER')"
            allow-create
            :auto-open-dropdown="false"
            class="flex-grow min-h-[2rem] px-2 py-1 rounded border border-solid bg-n-background border-n-weak dark:border-n-weak"
            @input="onTagInput"
            @blur="commitPendingTagInput"
            @on-click-outside="commitPendingTagInput"
          />
          <input
            v-else
            v-model="values"
            type="text"
            class="!mb-0"
            :placeholder="$t('FILTER.INPUT_PLACEHOLDER')"
          />
        </div>
        <NextButton
          icon="i-lucide-x"
          slate
          ghost
          class="flex-shrink-0"
          @click="removeFilter"
        />
      </div>
      <p v-if="translatedErrorMessage" class="filter-error">
        {{ translatedErrorMessage }}
      </p>
      <p
        v-if="shouldShowMultiValueHint"
        class="mt-1.5 text-xs text-n-slate-10 px-1 flex items-center justify-end gap-1"
      >
        <span
          class="font-semibold text-n-blue-10 bg-n-blue-3 dark:bg-n-blue-4/30 px-1.5 py-0.5 rounded"
          >{{ multiValueLogicBadge }}</span
        >
        <span>{{
          multiValueLogicBadge === $t('FILTER.QUERY_DROPDOWN_LABELS.OR')
            ? $t('FILTER.MULTI_VALUE_HINT_OR')
            : $t('FILTER.MULTI_VALUE_HINT_AND')
        }}</span>
      </p>
    </div>

    <div
      v-if="showQueryOperator"
      class="flex items-center justify-center relative my-2.5 mx-0"
    >
      <hr class="absolute w-full border-b border-solid border-n-weak" />
      <FilterSelect
        v-model="query_operator"
        :options="queryOperatorOptions"
        variant="outline"
        class="relative z-10"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.filter__answer--wrap {
  input {
    @apply bg-n-background mb-0 text-n-slate-12 border-n-weak;
  }
}

.filter-error {
  @apply text-n-ruby-9 dark:text-n-ruby-9 block my-1 mx-0;
}

.multiselect {
  @apply mb-0;
}

.automation-filter-input__select:deep(button) {
  @apply w-full justify-between;
}
</style>
